import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';

/**
 * Extension to preserve empty lines in TipTap editor
 * This ensures empty paragraphs are maintained and not removed
 */
export const PreserveEmptyLines = Extension.create({
  name: 'preserveEmptyLines',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('preserveEmptyLines'),
        
        // Transform pasted content to preserve empty paragraphs
        props: {
          transformPasted: (slice) => {
            return slice;
          },
          
          // Handle DOM parsing to preserve empty paragraphs
          transformPastedHTML: (html) => {
            // Replace empty <p> tags with <p> containing a zero-width space
            return html.replace(/<p><\/p>/g, '<p>&#8203;</p>');
          },
        },

        // Ensure empty paragraphs don't get removed by the editor
        filterTransaction: (transaction, state) => {
          // Allow all transactions - we don't want to block any
          return true;
        },

        // Add empty paragraph handling during document updates
        appendTransaction: (transactions, oldState, newState) => {
          // Only process if there were actual changes
          if (!transactions.some(tr => tr.docChanged)) {
            return null;
          }

          const tr = newState.tr;
          let modified = false;

          // Process the document to ensure empty paragraphs are preserved
          newState.doc.descendants((node, pos) => {
            if (node.type.name === 'paragraph' && node.content.size === 0) {
              // Check if this empty paragraph needs a zero-width space
              const currentContent = node.textContent;
              if (currentContent !== '\u200B' && currentContent === '') {
                // Insert zero-width space to preserve the paragraph
                const textNode = newState.schema.text('\u200B');
                tr.insert(pos + 1, textNode);
                modified = true;
              }
            }
          });

          return modified ? tr : null;
        },
      }),
    ];
  },

  addKeyboardShortcuts() {
    return {
      // Handle Enter key behavior
      'Enter': ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        // Check if we're at the end of an empty paragraph
        if (
          $from.parent.type.name === 'paragraph' && 
          $from.parent.textContent === '\u200B' &&
          $from.parentOffset === 1
        ) {
          // Create a new empty paragraph
          return editor.chain()
            .insertContentAt($from.pos, { type: 'paragraph' })
            .focus($from.pos + 2)
            .run();
        }

        // Default Enter behavior for other cases
        return false;
      },

      // Handle Shift+Enter for hard breaks
      'Shift-Enter': ({ editor }) => {
        return editor.chain().setHardBreak().run();
      },

      // Handle Backspace to properly handle empty paragraphs
      'Backspace': ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        // If we're in a paragraph with only a zero-width space
        if (
          $from.parent.type.name === 'paragraph' && 
          $from.parent.textContent === '\u200B' &&
          $from.parentOffset === 1
        ) {
          // Don't delete the zero-width space, instead delete the paragraph
          const pos = $from.before();
          if (pos > 0) {
            return editor.chain()
              .deleteRange({ from: pos, to: $from.after() })
              .run();
          }
        }

        // Default backspace behavior
        return false;
      },
    };
  },

  // Configure the extension to work with document structure
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph'],
        attributes: {
          'data-empty': {
            default: null,
            parseHTML: element => element.textContent === '' ? 'true' : null,
            renderHTML: attributes => {
              if (attributes['data-empty'] === 'true') {
                return {
                  'data-empty': 'true',
                };
              }
              return {};
            },
          },
        },
      },
    ];
  },
});